import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, ChevronDown, Check, LogOut, LayoutGrid } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useResellerCompany, Company } from '../contexts/ResellerCompanyContext';
import { RESELLER_ALL_COMPANY, RESELLER_ALL_TENANT_ID } from '../utils/resellerCompany';
import apiService from '../services/api';

interface ResellerCompanySwitcherProps {
  /**
   * 'header' (default): compact pill + dropdown for the top bar — hidden on mobile
   * widths since there isn't room for it alongside the hamburger/back/breadcrumb/profile.
   * 'drawer': full-width, always-expanded list for the mobile sidebar drawer, where
   * there's room to show it properly instead of cramming an icon into the header.
   */
  variant?: 'header' | 'drawer';
}

/**
 * Lets a reseller switch between "my own workspace" and any company they created, so
 * they can see that company's actual Sales/Bills/Purchases/etc. Unlike
 * MachineCompanySwitcher (which only scopes the Machines page), this scopes every
 * request the app makes while a company is selected — see api.ts.
 */
const ResellerCompanySwitcher: React.FC<ResellerCompanySwitcherProps> = ({ variant = 'header' }) => {
  const { user } = useAuth();
  const { viewedCompany, setViewedCompany } = useResellerCompany();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: ['reseller-companies'],
    queryFn: () => apiService.get('/admin/admins'),
    enabled: !!user?.isReseller
  });

  const companies = useMemo<Company[]>(() => {
    if (user?.isReseller) {
      const admins: any[] = (data as any)?.admins || [];
      return admins
        .filter((a) => a.role !== 'SUPERADMIN' && a.tenantId != null && a.tenantId === a.id)
        .map((a) => ({ tenantId: a.tenantId as number, name: a.companyName || a.name }))
        .sort((x, y) => x.name.localeCompare(y.name));
    }
    const list = Array.isArray(user?.companyAccessCompanies) ? user!.companyAccessCompanies : [];
    return list.map((c) => ({ tenantId: c.tenantId, name: c.name }));
  }, [user, data]);

  // Only relevant for team members — resellers have full read-write on everything
  // they view, so this map stays empty for them and no badge ever renders.
  const writeByTenantId = useMemo(() => {
    const map = new Map<number, boolean>();
    (user?.companyAccessCompanies || []).forEach((c) => map.set(c.tenantId, c.canWrite));
    return map;
  }, [user]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isTeamMember = !user?.isReseller && (user?.companyAccess?.length ?? 0) > 0;
  if (!user?.isReseller && !isTeamMember) return null;

  // Resellers get a merged "all my companies" view (own workspace + every company they
  // created). Team members don't — their access is a per-company grant list.
  const showAllOption = !!user?.isReseller && companies.length > 0;
  const isAll = viewedCompany?.tenantId === RESELLER_ALL_TENANT_ID;

  const choose = (company: Company | null) => {
    setOpen(false);
    if (company?.tenantId === viewedCompany?.tenantId) return;
    setViewedCompany(company);
    // Every domain's data was cached for the previous scope — refetch through the new one.
    queryClient.invalidateQueries();
  };

  if (variant === 'drawer') {
    return (
      <div className="px-3 pb-3">
        <p className="px-3 pb-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">Viewing</p>
        <div className="space-y-0.5">
          <button
            onClick={() => choose(null)}
            className={`flex items-center justify-between w-full px-3 py-2.5 text-sm text-left rounded-lg transition-colors ${
              !viewedCompany ? 'bg-[#F2F2F7] text-[#0055a9] font-semibold' : 'text-[#0E131B] hover:bg-gray-50'
            }`}
          >
            <span className="flex items-center gap-2"><LogOut className="h-4 w-4" /> My workspace</span>
            {!viewedCompany && <Check className="h-4 w-4 flex-shrink-0" />}
          </button>
          {showAllOption && (
            <button
              onClick={() => choose(RESELLER_ALL_COMPANY)}
              className={`flex items-center justify-between w-full px-3 py-2.5 text-sm text-left rounded-lg transition-colors ${
                isAll ? 'bg-[#F2F2F7] text-[#0055a9] font-semibold' : 'text-[#0E131B] hover:bg-gray-50'
              }`}
            >
              <span className="flex items-center gap-2"><LayoutGrid className="h-4 w-4" /> All my companies</span>
              {isAll && <Check className="h-4 w-4 flex-shrink-0" />}
            </button>
          )}
          {companies.map((c) => {
            const active = viewedCompany?.tenantId === c.tenantId;
            return (
              <button
                key={c.tenantId}
                onClick={() => choose(c)}
                className={`flex items-center justify-between w-full px-3 py-2.5 text-sm text-left rounded-lg transition-colors ${
                  active ? 'bg-[#F2F2F7] text-[#0055a9] font-semibold' : 'text-[#0E131B] hover:bg-gray-50'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 flex-shrink-0" /> {c.name}
                  {writeByTenantId.get(c.tenantId) === false && (
                    <span className="text-xs text-gray-400">(Read-only)</span>
                  )}
                </span>
                {active && <Check className="h-4 w-4 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="View company"
        className="flex items-center gap-2 h-11 px-3 rounded-xl border bg-[#F2F2F7] border-[#E8EBF1] text-[#1B202A] hover:bg-[#EAEBF0] transition-colors"
      >
        <Building2 className="h-5 w-5 flex-shrink-0 text-[#0055a9]" />
        <span className="text-sm font-semibold truncate max-w-[9rem] hidden sm:block">
          {viewedCompany?.name || 'My workspace'}
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="fixed left-2 right-2 top-[80px] sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-[240px] bg-white rounded-xl shadow-[0_7px_29px_rgba(100,100,111,0.2)] border border-[#E1E6EF] py-1 z-[1002] max-h-[70vh] overflow-y-auto">
          <button
            onClick={() => choose(null)}
            className={`flex items-center justify-between w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 transition-colors ${
              !viewedCompany ? 'text-[#0055a9] font-semibold' : 'text-[#0E131B]'
            }`}
          >
            <span className="flex items-center gap-2"><LogOut className="h-4 w-4" /> My workspace</span>
            {!viewedCompany && <Check className="h-4 w-4 flex-shrink-0" />}
          </button>
          {showAllOption && (
            <button
              onClick={() => choose(RESELLER_ALL_COMPANY)}
              className={`flex items-center justify-between w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 transition-colors ${
                isAll ? 'text-[#0055a9] font-semibold' : 'text-[#0E131B]'
              }`}
            >
              <span className="flex items-center gap-2"><LayoutGrid className="h-4 w-4" /> All my companies</span>
              {isAll && <Check className="h-4 w-4 flex-shrink-0" />}
            </button>
          )}
          <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
            My Companies
          </div>
          {companies.map((c) => {
            const active = viewedCompany?.tenantId === c.tenantId;
            return (
              <button
                key={c.tenantId}
                onClick={() => choose(c)}
                className={`flex items-center justify-between w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 transition-colors ${
                  active ? 'text-[#0055a9] font-semibold' : 'text-[#0E131B]'
                }`}
              >
                <span className="truncate">
                  {c.name}
                  {writeByTenantId.get(c.tenantId) === false && (
                    <span className="text-xs text-gray-400 ml-1">(Read-only)</span>
                  )}
                </span>
                {active && <Check className="h-4 w-4 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ResellerCompanySwitcher;
